import React from 'react'
import { createPortal } from 'react-dom'
import type { Editor } from '@tiptap/core'
import type { AiChange } from './ai-changes-extension'
import { offset, shift, useFloating } from '@floating-ui/react'

interface ReviewChangePopoverProps {
  editor: Editor
  element: HTMLElement | null
  selectedChange: AiChange | null
}

export const ReviewChangePopover: React.FC<ReviewChangePopoverProps> = ({
  editor,
  element,
  selectedChange,
}) => {
  const { refs, floatingStyles } = useFloating({
    open: Boolean(selectedChange && element),
    placement: 'bottom-start',
    middleware: [offset(8), shift({ padding: 8 })],
  })

  React.useEffect(() => {
    refs.setReference(element)
  }, [element, refs])

  const handleAcceptChange = () => {
    if (selectedChange) {
      editor.commands.acceptAiChange(selectedChange.id)
    }
  }

  const handleRejectChange = () => {
    if (selectedChange) {
      editor.commands.rejectAiChange(selectedChange.id)
    }
  }

  const getChangeTypeLabel = (type: string) => {
    switch (type) {
      case 'added':
        return 'Added'
      case 'removed':
        return 'Removed'
      case 'modified':
        return 'Modified'
      default:
        return 'Changed'
    }
  }

  const getChangeTypeColor = (type: string) => {
    switch (type) {
      case 'added':
        return 'text-green-600 bg-green-50 border-green-200'
      case 'removed':
        return 'text-red-600 bg-red-50 border-red-200'
      case 'modified':
        return 'text-blue-600 bg-blue-50 border-blue-200'
      default:
        return 'text-gray-600 bg-gray-50 border-border'
    }
  }

  if (!selectedChange || !element) {
    return null
  }

  return createPortal(
    <div
      ref={refs.setFloating}
      style={{
        ...floatingStyles,
        position: 'fixed',
        zIndex: 9999,
      }}
      className="ai-change-tooltip bg-white rounded-lg border border-border shadow-lg p-4 space-y-2"
    >
      <div className="ai-change-tooltip__header">
        <div className="flex items-center gap-2">
          <span
            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getChangeTypeColor(
              selectedChange.type
            )}`}
          >
            {getChangeTypeLabel(selectedChange.type)}
          </span>
          <span className="text-xs text-gray-500">
            Position: {selectedChange.newRange.from}-{selectedChange.newRange.to}
          </span>
        </div>
      </div>

      {selectedChange.content && (
        <div className="ai-change-tooltip__content">
          <div className="text-xs text-gray-500 mb-1">Content:</div>
          <div className="font-mono text-sm bg-gray-50 dark:bg-gray-800 p-2 rounded border">
            "{selectedChange.content}"
          </div>
        </div>
      )}

      <div className="ai-change-tooltip__actions">
        <button
          onClick={handleAcceptChange}
          className="primary"
          title="Accept this change"
        >
          ✓ Accept
        </button>
        <button
          onClick={handleRejectChange}
          className="destructive"
          title="Reject this change"
        >
          ✗ Reject
        </button>
      </div>
    </div>,
    document.body
  )
} 